import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Processing...');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Detect cancelled / error case (user denied consent at Google)
      const errorFromHash = location.hash ? new URLSearchParams(location.hash.replace('#', '')).get('error') : null;
      const errorFromQuery = location.search ? new URLSearchParams(location.search).get('error') : null;
      const oauthError = errorFromHash || errorFromQuery;
      if (oauthError) {
        setStatus('Sign-in was cancelled. Redirecting to login...');
        setTimeout(() => navigate('/login', { replace: true }), 1200);
        return;
      }

      // Extract session_id from URL fragment or query params
      let sessionId = null;
      
      // Check hash first (e.g., /dashboard#session_id=xxx)
      if (location.hash) {
        const params = new URLSearchParams(location.hash.replace('#', ''));
        sessionId = params.get('session_id');
      }
      
      // Also check query params as fallback
      if (!sessionId && location.search) {
        const params = new URLSearchParams(location.search);
        sessionId = params.get('session_id');
      }

      if (!sessionId) {
        console.error('No session_id found in URL');
        setStatus('No session found. Redirecting...');
        setTimeout(() => navigate('/login', { replace: true }), 1000);
        return;
      }

      try {
        setStatus('Authenticating with Google...');
        
        // Exchange session_id for our session
        const userData = await login(sessionId);
        
        setStatus('Success! Redirecting to dashboard...');
        
        // Small delay to ensure cookie is set before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Clear the hash from URL to prevent re-processing
        window.history.replaceState(null, '', window.location.pathname);
        
        // Navigate to dashboard with user data
        navigate('/dashboard', { 
          replace: true, 
          state: { user: userData, fromOAuth: true } 
        });
      } catch (error) {
        console.error('Auth error:', error);
        setStatus('Authentication failed. Redirecting...');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      }
    };

    processAuth();
  }, [login, navigate, location.hash, location.search, checkAuth]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
        <p className="text-slate-600">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
